import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { STORE_URLS, STORE_CONFIG, UPDATE_STORAGE_KEYS } from '../shared/app-store-config';

class AppUpdateService {
    constructor() {
        this.isInitialized = false;
    }

    /**
     * Initialisiert den AppUpdateService
     */
    async initialize() {
        if (this.isInitialized || !Capacitor.isNativePlatform()) {
            return;
        }

        console.log('AppUpdateService: Initializing...');
        this.isInitialized = true;
        
        // Prüfe beim App-Start auf Updates, falls aktiviert
        if (STORE_CONFIG.autoCheckOnStart) {
            await this.checkForUpdateWithThrottling();
        }
    }

    /**
     * Prüft auf Updates mit Throttling (verhindert zu häufige Prüfungen)
     */
    async checkForUpdateWithThrottling() {
        try {
            const lastCheck = localStorage.getItem(UPDATE_STORAGE_KEYS.lastUpdateCheck);
            const now = Date.now();
            
            if (lastCheck && (now - parseInt(lastCheck)) < STORE_CONFIG.checkInterval) {
                console.log('AppUpdateService: Update check skipped due to throttling');
                return;
            }
            
            localStorage.setItem(UPDATE_STORAGE_KEYS.lastUpdateCheck, now.toString());
            await this.checkForUpdate();
        } catch (error) {
            console.error('AppUpdateService: Error in throttled update check:', error);
        }
    }

    /**
     * Prüft, ob ein App-Update verfügbar ist
     */
    async checkForUpdate() {
        try {
            if (!Capacitor.isNativePlatform()) {
                console.log('AppUpdateService: Not running on native platform, skipping update check');
                return false;
            }

            console.log('AppUpdateService: Checking for app updates...');
            
            const result = await AppUpdate.getAppUpdateInfo();
            console.log('AppUpdateService: Update info:', result);

            if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
                const shouldShow = this.shouldShowUpdateDialog(result);
                if (shouldShow) {
                    await this.showUpdateDialog(result);
                    return true;
                }
            } else {
                console.log('AppUpdateService: No update available');
            }
            return false;
        } catch (error) {
            console.error('AppUpdateService: Error checking for updates:', error);
            return false;
        }
    }

    /**
     * Bestimmt, ob der Update-Dialog angezeigt werden soll
     */
    shouldShowUpdateDialog(updateInfo) {
        const lastDialogShown = localStorage.getItem(UPDATE_STORAGE_KEYS.lastDialogShown);
        const dismissedVersion = localStorage.getItem(UPDATE_STORAGE_KEYS.dismissedVersion);
        const now = Date.now();
        
        // Wenn diese Version bereits abgelehnt wurde, nicht erneut anzeigen
        if (dismissedVersion === updateInfo.availableVersion) {
            return false;
        }
        
        // Minimaler Abstand zwischen Dialogen beachten
        if (lastDialogShown && (now - parseInt(lastDialogShown)) < STORE_CONFIG.minDialogInterval) {
            return false;
        }
        
        return true;
    }

    /**
     * Zeigt den Update-Dialog an
     */
    async showUpdateDialog(updateInfo) {
        try {
            const lastDialogShown = Date.now().toString();
            localStorage.setItem(UPDATE_STORAGE_KEYS.lastDialogShown, lastDialogShown);
            
            const result = await this.createUpdateDialog(updateInfo);
            
            if (result === 'update') {
                await this.openAppStore();
            } else if (result === 'dismiss') {
                // Diese Version als abgelehnt markieren
                localStorage.setItem(UPDATE_STORAGE_KEYS.dismissedVersion, updateInfo.availableVersion || 'unknown');
            }
        } catch (error) {
            console.error('AppUpdateService: Error showing update dialog:', error);
        }
    }

    /**
     * Erstellt und zeigt den Update-Dialog
     */
    createUpdateDialog(updateInfo) {
        return new Promise((resolve) => {
            // Erstelle Modal-Dialog
            const modal = document.createElement('div');
            modal.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: rgba(0, 0, 0, 0.5);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 10000;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            `;

            const dialog = document.createElement('div');
            dialog.style.cssText = `
                background: white;
                border-radius: 12px;
                padding: 24px;
                max-width: 340px;
                width: 90%;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                text-align: center;
            `;

            const title = document.createElement('h3');
            title.textContent = 'App-Update verfügbar';
            title.style.cssText = `
                margin: 0 0 16px 0;
                color: #333;
                font-size: 18px;
                font-weight: 600;
            `;

            const message = document.createElement('p');
            message.innerHTML = updateInfo.currentVersion && updateInfo.availableVersion 
                ? `Eine neue Version (<strong>${updateInfo.availableVersion}</strong>) ist verfügbar.<br><br>Sie verwenden derzeit Version <strong>${updateInfo.currentVersion}</strong>.`
                : 'Eine neue Version der App ist verfügbar. Möchten Sie jetzt aktualisieren?';
            message.style.cssText = `
                margin: 0 0 24px 0;
                color: #666;
                font-size: 14px;
                line-height: 1.4;
            `;

            const buttonContainer = document.createElement('div');
            buttonContainer.style.cssText = `
                display: flex;
                gap: 12px;
                justify-content: center;
                flex-wrap: wrap;
            `;

            const laterButton = document.createElement('button');
            laterButton.textContent = 'Später';
            laterButton.style.cssText = `
                padding: 10px 20px;
                border: 1px solid #ddd;
                background: white;
                color: #666;
                border-radius: 8px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 500;
                min-width: 80px;
            `;

            const dismissButton = document.createElement('button');
            dismissButton.textContent = 'Diese Version ignorieren';
            dismissButton.style.cssText = `
                padding: 10px 20px;
                border: 1px solid #ddd;
                background: white;
                color: #999;
                border-radius: 8px;
                cursor: pointer;
                font-size: 12px;
                font-weight: 400;
                min-width: 140px;
            `;

            const updateButton = document.createElement('button');
            updateButton.textContent = 'Jetzt aktualisieren';
            updateButton.style.cssText = `
                padding: 10px 20px;
                border: none;
                background: #007AFF;
                color: white;
                border-radius: 8px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 500;
                min-width: 120px;
            `;

            // Event Listeners
            laterButton.addEventListener('click', () => {
                document.body.removeChild(modal);
                resolve('later');
            });

            dismissButton.addEventListener('click', () => {
                document.body.removeChild(modal);
                resolve('dismiss');
            });

            updateButton.addEventListener('click', () => {
                document.body.removeChild(modal);
                resolve('update');
            });

            // Dialog zusammenbauen
            buttonContainer.appendChild(laterButton);
            buttonContainer.appendChild(dismissButton);
            buttonContainer.appendChild(updateButton);
            dialog.appendChild(title);
            dialog.appendChild(message);
            dialog.appendChild(buttonContainer);
            modal.appendChild(dialog);
            document.body.appendChild(modal);

            // Focus auf Update-Button
            updateButton.focus();
        });
    }

    /**
     * Öffnet den App Store für Updates
     */
    async openAppStore() {
        try {
            await AppUpdate.openAppStore();
        } catch (error) {
            console.error('AppUpdateService: Error opening app store:', error);
            // Fallback: Versuche mit App-Info und konfigurierten URLs
            await this.openAppStoreFallback();
        }
    }

    /**
     * Fallback-Methode zum Öffnen des App Stores
     */
    async openAppStoreFallback() {
        try {
            const platform = Capacitor.getPlatform();
            let storeUrl;
            
            if (platform === 'ios') {
                storeUrl = STORE_URLS.ios;
            } else if (platform === 'android') {
                storeUrl = STORE_URLS.android;
            }

            if (storeUrl) {
                window.open(storeUrl, '_system');
            } else {
                throw new Error('No store URL configured for platform: ' + platform);
            }
        } catch (error) {
            console.error('AppUpdateService: Error in fallback method:', error);
            throw error;
        }
    }

    /**
     * Manuelle Update-Prüfung (kann von der UI aufgerufen werden)
     */
    async checkForUpdateManually() {
        // Bei manueller Prüfung ignorieren wir das Throttling
        return await this.checkForUpdate();
    }

    /**
     * Setzt die Update-Einstellungen zurück
     */
    resetUpdateSettings() {
        localStorage.removeItem(UPDATE_STORAGE_KEYS.lastUpdateCheck);
        localStorage.removeItem(UPDATE_STORAGE_KEYS.lastDialogShown);
        localStorage.removeItem(UPDATE_STORAGE_KEYS.dismissedVersion);
        console.log('AppUpdateService: Update settings reset');
    }

    /**
     * Gibt Informationen über die aktuelle App zurück
     */
    async getAppInfo() {
        try {
            const appInfo = await App.getInfo();
            return {
                name: appInfo.name,
                id: appInfo.id,
                version: appInfo.version,
                build: appInfo.build
            };
        } catch (error) {
            console.error('AppUpdateService: Error getting app info:', error);
            return null;
        }
    }

    /**
     * Gibt Update-Statistiken zurück
     */
    getUpdateStats() {
        const lastCheck = localStorage.getItem(UPDATE_STORAGE_KEYS.lastUpdateCheck);
        const lastDialogShown = localStorage.getItem(UPDATE_STORAGE_KEYS.lastDialogShown);
        const dismissedVersion = localStorage.getItem(UPDATE_STORAGE_KEYS.dismissedVersion);

        return {
            lastCheck: lastCheck ? new Date(parseInt(lastCheck)) : null,
            lastDialogShown: lastDialogShown ? new Date(parseInt(lastDialogShown)) : null,
            dismissedVersion: dismissedVersion || null,
            autoCheckEnabled: STORE_CONFIG.autoCheckOnStart
        };
    }
}

// Singleton-Instanz
const appUpdateService = new AppUpdateService();
export default appUpdateService;
